import { useEffect, useMemo, useRef } from 'react';
import { logError } from 'setup/logger/logError';
import { EntityName } from 'shared/domain/entityName';
import type { IssueModel } from 'shared/domain/issue/issueModel';
import type { IssueReferenceName, IssueReferences } from 'shared/domain/issue/issueReferences';
import { levelClient } from 'shared/domain/level/levelClient';
import { siteClient } from 'shared/domain/site/siteClient';
import { type EntityByIdRead, useEntityById } from 'store/entityCollection';
import { useLevels } from 'store/level';
import { useProcesses } from 'store/process';
import { useSites } from 'store/site';
import { getIssueReferenceIds, readIssueProcess, resolveIssueReferences } from './model';
import type { IssueReferenceIds, IssueReferencesState } from './types';

export type { IssueReferencesState } from './types';

/** Logs once per issue the reference it cannot be shown without, so the cause can be found. */
function useMissingReferenceLog(
  issueId: string | undefined,
  referenceIds: IssueReferenceIds,
  missing: IssueReferenceName | undefined
): void {
  // Every issue logged while the view is open: going back to one (A → B → A) does not log it again.
  const loggedIssueIds = useRef(new Set<string>());

  useEffect(() => {
    if (!issueId || !missing || loggedIssueIds.current.has(issueId)) {
      return;
    }
    loggedIssueIds.current.add(issueId);
    logError('issue-reference-missing', { issueId, reference: missing, referenceId: referenceIds[missing] });
  }, [issueId, referenceIds, missing]);
}

/** The process of the issue; read once the processes are, as the store starts empty and not loading. */
function useIssueProcess(processId: string | undefined): EntityByIdRead<IssueReferences['process']> {
  const { processes, isReady } = useProcesses();
  return useMemo(() => readIssueProcess(processes, isReady, processId), [processes, isReady, processId]);
}

/**
 * The site, level and process of the issue: from the stores when they hold
 * them, otherwise read by id from the worker (archived ones too, synced when
 * created since the last download).
 */
export function useIssueReferences(issueModel: IssueModel | undefined): IssueReferencesState {
  const referenceIds = useMemo(() => getIssueReferenceIds(issueModel), [issueModel]);
  const { items: sites } = useSites();
  const { items: levels } = useLevels();
  const site = useEntityById({
    id: referenceIds.site,
    client: siteClient,
    items: sites,
    entityName: EntityName.sites,
  });
  const level = useEntityById({
    id: referenceIds.level,
    client: levelClient,
    items: levels,
    entityName: EntityName.levels,
  });
  const process = useIssueProcess(referenceIds.process);

  const state = useMemo(
    () => (issueModel ? resolveIssueReferences({ site, level, process }) : {}),
    [issueModel, site, level, process]
  );
  useMissingReferenceLog(issueModel?._id, referenceIds, state.missing);
  return state;
}
